import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, TrendingUp, Clock, Users, Award } from 'lucide-react';
import { clientImpact, images } from '../data/mock';

const metricIcons = {
  "Productivity Boost": TrendingUp,
  "Faster Testing Cycles": Clock,
  "Hiring Time Reduction": Users,
  "Projects Delivered": Award,
};

const ClientImpactPage = () => {
  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={images.partnership}
            alt="Client Success"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/90 to-white/80" />
        </div>
        <div className="container-custom relative z-10">
          <div className="max-w-3xl">
            <span className="text-[#F58B23] font-medium text-sm uppercase tracking-wider mb-4 block">
              Results That Matter
            </span>
            <h1 className="text-4xl md:text-5xl font-serif font-bold text-[#1A1A1A] mb-6">
              Client Impact
            </h1>
            <p className="text-lg text-[#6B7280] leading-relaxed">
              We measure our success by the tangible outcomes we deliver for our clients. 
              Here's a snapshot of the value we've created.
            </p>
          </div>
        </div>
      </section>

      {/* Metrics Section */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {clientImpact.metrics.map((metric, index) => {
              const IconComponent = metricIcons[metric.label] || TrendingUp;
              return (
                <div
                  key={index}
                  className="bg-[#FAFAF8] rounded-2xl p-8 border border-gray-100 text-center card-hover"
                >
                  <div className="icon-container mx-auto mb-6">
                    <IconComponent className="w-6 h-6 text-[#F58B23]" />
                  </div>
                  <div className="text-4xl md:text-5xl font-serif font-bold text-[#F58B23] mb-2">
                    {metric.value}
                  </div>
                  <div className="text-[#1A1A1A] font-semibold mb-2">
                    {metric.label}
                  </div>
                  <p className="text-[#6B7280] text-sm">
                    {metric.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Feature Image */}
      <section className="py-12 bg-[#FAFAF8]">
        <div className="container-custom">
          <div className="rounded-2xl overflow-hidden">
            <img
              src={images.digitalTransformation}
              alt="Digital Transformation Success"
              className="w-full h-64 md:h-80 object-cover"
            />
          </div>
        </div>
      </section>

      {/* Case Studies Section */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#1A1A1A] mb-4">
              Success Stories
            </h2>
            <p className="text-[#6B7280] max-w-2xl mx-auto">
              Real transformations, real results.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {clientImpact.caseStudies.map((study, index) => (
              <div
                key={index}
                className="bg-[#FAFAF8] rounded-2xl p-8 border border-gray-100 card-hover"
              >
                <div className="inline-block px-3 py-1 bg-[#FFF4E6] text-[#F58B23] text-xs font-medium rounded-full mb-4">
                  {study.industry}
                </div>
                <h3 className="text-xl font-serif font-semibold text-[#1A1A1A] mb-4">
                  {study.title}
                </h3>
                <p className="text-[#6B7280] text-sm leading-relaxed">
                  {study.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#1A1A1A]">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-6">
              Ready to Create Your Success Story?
            </h2>
            <p className="text-gray-400 mb-8 text-lg">
              Let's discuss how we can deliver similar results for your organization.
            </p>
            <Link to="/contact">
              <Button
                size="lg"
                className="bg-[#F58B23] hover:bg-[#D97706] text-white font-medium px-10"
              >
                Schedule a Consultation
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ClientImpactPage;
